import type { CustomThemeConfig } from 'tailwindcss/types/config';

/**
 * Layout Documentation
 * - Inner Padding default: px-8 tablet:px-18 desktop:px-32
 */

export const palmbergTheme: Partial<
  CustomThemeConfig & { extend: Partial<CustomThemeConfig> }
> = {
  extend: {
    screens: {
      tablet: '824px',
      desktop: '1440px',
      xl: '1920px',
      xxl: '2560px',
    },
    spacing: {
      22: '82px',
      23: '88px',
      18: '70px',
      8: '30px',
      9: '35px',
      '4.5': '18px',
    },
    padding: {
      22: '82px',
      23: '88px',
      18: '70px',
      8: '30px',
      9: '35px',
      13: '50px',
      '4.5': '18px',
    },
    gap: {
      22: '82px',
      23: '88px',
      18: '70px',
      8: '30px',
      9: '35px',
      13: '50px',
      '4.5': '18px',
    },
    margin: {
      22: '82px',
      23: '88px',
      18: '70px',
      8: '30px',
      9: '35px',
      13: '50px',
      '4.5': '18px',
    },
    height: {
      22: '82px',
      23: '88px',
      18: '70px',
      8: '30px',
      9: '35px',
      13: '50px',
      '4.5': '18px',
    },
    width: {
      22: '82px',
      23: '88px',
      18: '70px',
      8: '30px',
      9: '35px',
      13: '50px',
      '4.5': '18px',
    },
    colors: {
      // Main Colors - using rgb() with CSS variables for Tailwind opacity modifier support
      'palmberg-grn': 'rgb(var(--Palmberg-Grn) / <alpha-value>)', // #56af31
      'palmberg-dunkel-grn': 'rgb(var(--Palmberg-Dunkel-Grn) / <alpha-value>)', // #0d4d4b
      'palmberg-grau': 'rgb(var(--Palmberg-Grau) / <alpha-value>)', // #ededed
      'palmberg-grau2': 'rgb(var(--Palmberg-Grau2) / <alpha-value>)', // #f2f2f2
      'palmberg-beige': 'rgb(var(--Palmberg-Beige) / <alpha-value>)', // #ebe6d9
      white: 'rgb(var(--White) / <alpha-value>)', // #ffffff
      black: 'rgb(var(--Schwarz) / <alpha-value>)', // #000000
      'inperpective-beige': 'rgb(var(--Inperpective-Beige) / <alpha-value>)', // #fffaf1
      'grn-hover': 'rgb(var(--GRN-HOVER) / <alpha-value>)', // #053a38
      'inperspective-violet': 'rgb(var(--Inperspective-Violet) / <alpha-value>)', // #941fa6
      'inperspective-hellviolet':
        'rgb(var(--Inperspective-Hellviolet) / <alpha-value>)', // #fbe2ff
      'inperspective-rot': 'rgb(var(--Inperspective-Rot) / <alpha-value>)', // #b92e08
      'inperspective-hellrot': 'rgb(var(--Inperspective-Hellrot) / <alpha-value>)', // #ffdfd2
      'inperspective-blau': 'rgb(var(--Inperspective-Blau) / <alpha-value>)', // #2e19b5
      'inperspective-hellblau': 'rgb(var(--Inperspective-Hellblau) / <alpha-value>)', // #e3ecff
      'inperspective-hellgrn': 'rgb(var(--Inperspective-Hellgrn) / <alpha-value>)', // #ddf8d0
      grau: 'rgb(var(--Grau) / <alpha-value>)', // #949494
      'orgatec-blue': 'rgb(var(--Orgatec-Blue) / <alpha-value>)', // #009BF6
      'orgatec-magenta': 'rgb(var(--Orgatec-Magenta) / <alpha-value>)', // #C744E8
      'orgatec-orange': 'rgb(var(--Orgatec-Orange) / <alpha-value>)', // #FF4E00
    },
    boxShadow: {
      'btn-default': 'inset 0 0 0 0.063rem var(--btn-shadow-color, black)',
      'btn-focus': 'inset 0 0 0 3px var(--btn-shadow-color, black)',
      'btn-focus-visible': 'inset 0 0 0 3px var(--btn-shadow-color, black)',
    },
    fontFamily: {
      sans: ['var(--font-Din)', 'Helvetica', 'Arial', 'sans-serif'],
    },
    fontSize: {
      html: ['1rem', '1.625rem'],
      base: ['1rem', '1.625rem'],
      btnBaseFont: [
        '1rem',
        {
          lineHeight: '1.25rem',
          letterSpacing: '0.05rem',
        },
      ],
      subline1Mobile: ['1.5rem', { lineHeight: '1.625rem', fontWeight: '600' }],
      subline1Desktop: [
        '1.625rem',
        {
          lineHeight: '1.75rem',
          fontWeight: '600',
        },
      ],
      p1CopyMobile: [
        '1.125rem',
        {
          lineHeight: '1.75rem',
          fontWeight: '300',
        },
      ],
      p1CopySemiboldMobile: [
        '1.125rem',
        {
          lineHeight: '1.75rem',
          fontWeight: '600',
        },
      ],
      p1CopyDesktop: [
        '1rem',
        {
          lineHeight: '1.625rem',
          fontWeight: '300',
        },
      ],
      p1CopySemiboldDesktop: [
        '1rem',
        {
          lineHeight: '1.625rem',
          fontWeight: '600',
        },
      ],
      p2CopyMobile: [
        '0.75rem',
        {
          lineHeight: '1.125rem',
          letterSpacing: '0.015rem',
          fontWeight: '300',
        },
      ],
      p2CopyDesktop: [
        '0.75rem',
        {
          lineHeight: '1.125rem',
          letterSpacing: '0.015rem',
          fontWeight: '300',
        },
      ],
      p3CaptionMobile: [
        '0.75rem',
        {
          lineHeight: '0.875rem',
          letterSpacing: '0.015rem',
          fontWeight: '400',
        },
      ],
      p3CaptionDesktop: [
        '0.875rem',
        {
          lineHeight: '1rem',
          letterSpacing: '0.0175rem',
          fontWeight: '400',
        },
      ],
      h5Mobile: [
        '1.375rem',
        {
          lineHeight: '1.625rem',
          fontWeight: '400',
        },
      ],
      h5Desktop: [
        '1.375rem',
        {
          lineHeight: '1.5rem',
          fontWeight: '400',
        },
      ],
      h4Mobile: [
        '1.375rem',
        {
          lineHeight: '1.5rem',
          fontWeight: '600',
        },
      ],
      h4Desktop: [
        '1.375rem',
        {
          lineHeight: '1.5rem',
          fontWeight: '600',
        },
      ],
      h3Mobile: [
        '1.6875rem',
        {
          lineHeight: '1.875rem',
          fontWeight: '300',
        },
      ],
      h3Desktop: [
        '1.6875rem',
        {
          lineHeight: '1.875rem',
          fontWeight: '300',
        },
      ],
      h2Mobile: [
        '2.8125rem',
        {
          lineHeight: '2.8125rem',
          fontWeight: '300',
        },
      ],
      h2Desktop: [
        '2.8125rem',
        {
          lineHeight: '2.8125rem',
          fontWeight: '300',
        },
      ],
      h1Mobile: [
        '3.4375rem',
        {
          lineHeight: '3.4375rem',
          fontWeight: '300',
        },
      ],
      h1Desktop: [
        '4.375rem',
        {
          lineHeight: '4.375rem',
          fontWeight: '300',
        },
      ],
      subline: [
        '1.625rem',
        {
          lineHeight: '1.75rem',
          fontWeight: '300',
        },
      ],
      navigationFooter: [
        '0.875rem',
        {
          lineHeight: '1.3125rem',
          letterSpacing: '0.04375rem',
          fontWeight: '400',
        },
      ],
      buttonFont: [
        '1rem',
        {
          lineHeight: '1.25rem',
          letterSpacing: '0.05rem',
          fontWeight: '400',
        },
      ],
    },
  },
};
